import { useState } from "react";
import InventoryUploadField from "./InventoryUploadField";
import ParkingYardSearchSection from "./ParkingYardSearchSection";
import { fileAssetUrl } from "../../../utils/fileAssetUrl";
import {
  INVENTORY_IMAGE_CATEGORIES,
  INVENTORY_PDF_CATEGORIES,
  INVENTORY_UPLOAD_LIMITS,
  INVENTORY_VIDEO_CATEGORIES,
  capInventoryFiles,
  categoryFromStoredPath,
  createInitialCategorySlots,
  createInventorySlotId,
  filesForSlot,
  inventoryFileKey,
  mergeInventoryFiles,
  remainingInventorySlots,
  tagInventoryCategory,
} from "./inventoryUploadUtils";

function savedPathsForCategory(paths, meta, categoryId) {
  return (paths || []).filter(
    (storedPath) => categoryFromStoredPath(storedPath, meta) === categoryId
  );
}

function CategoryBoxes({
  categories,
  slots,
  files,
  savedPaths,
  meta,
  kind,
  accept,
  chooseLabel,
  existingCount,
  limit,
  submitting,
  onAddFiles,
  onRemoveFile,
  onAddBox,
  onRemoveBox,
}) {
  const remaining = remainingInventorySlots(existingCount + files.length, limit);

  return (
    <div className="inv-upload-grid">
      {categories.map((category) => {
        const categorySlots = slots[category.id] || [];
        const saved = savedPathsForCategory(savedPaths, meta, category.id);
        return categorySlots.map((slotId, index) => (
          <div key={slotId} className="inv-slot-wrap">
            {index === 0 && saved.length > 0 ? (
              <div className="inv-saved-inline">
                {saved.map((path, savedIndex) => {
                  const src = fileAssetUrl(path);
                  return kind === "image" ? (
                    <a key={`${path}-${savedIndex}`} href={src} target="_blank" rel="noreferrer">
                      <img src={src} alt={`${category.label} saved ${savedIndex + 1}`} />
                    </a>
                  ) : (
                    <a key={`${path}-${savedIndex}`} href={src} target="_blank" rel="noreferrer">
                      Saved {savedIndex + 1}
                    </a>
                  );
                })}
              </div>
            ) : null}
            <InventoryUploadField
              title={index === 0 ? category.label : `${category.label} #${index + 1}`}
              chooseLabel={chooseLabel}
              addMoreLabel={`+ Add More ${category.label}`}
              accept={accept}
              kind={kind}
              files={filesForSlot(files, category.id, slotId)}
              disabled={submitting}
              canAddMore={remaining > 0}
              canRemoveBox={categorySlots.length > 1}
              onAddFiles={(picked) => onAddFiles(picked, category.id, slotId)}
              onRemoveFile={onRemoveFile}
              onAddBox={() => onAddBox(category.id)}
              onRemoveBox={() => onRemoveBox(category.id, slotId)}
            />
          </div>
        ));
      })}
    </div>
  );
}

export default function InventoryUploadFormSection({
  confirmation,
  imageFiles,
  videoFiles,
  pdfFiles,
  setImageFiles,
  setVideoFiles,
  setPdfFiles,
  submitting,
  success,
  onSubmit,
  title = "Repossessed Assets",
  intro,
  submitLabel = "Submit inventory",
  selectedYard = null,
  onSelectYard,
  parkingYardRequired = true,
  showSavedPreviews = true,
}) {
  const existingImages = confirmation?.inventoryImages?.length || 0;
  const existingVideos = confirmation?.inventoryVideos?.length || 0;
  const existingPdfs = confirmation?.inventoryPdfs?.length || 0;
  const meta = confirmation?.inventoryFileMeta || [];

  const [imageSlots, setImageSlots] = useState(() =>
    createInitialCategorySlots(INVENTORY_IMAGE_CATEGORIES)
  );
  const [videoSlots, setVideoSlots] = useState(() =>
    createInitialCategorySlots(INVENTORY_VIDEO_CATEGORIES)
  );
  const [pdfSlots, setPdfSlots] = useState(() =>
    createInitialCategorySlots(INVENTORY_PDF_CATEGORIES)
  );

  const addFiles = (setter, existingCount, limit, incoming, categoryId, slotId) => {
    setter((prev) => {
      const tagged = tagInventoryCategory(incoming, categoryId, slotId);
      const merged = mergeInventoryFiles(prev, tagged);
      const maxPending = remainingInventorySlots(existingCount, limit);
      const { files, trimmed } = capInventoryFiles(merged, maxPending);
      if (trimmed > 0) {
        alert("File limit reached for this type. Extra file(s) were not added.");
      }
      return files;
    });
  };

  const removeFile = (setter, file) => {
    const key = inventoryFileKey(file);
    setter((prev) => prev.filter((item) => inventoryFileKey(item) !== key));
  };

  const addBox = (setSlots, existingCount, pendingCount, limit, categoryId) => {
    const remaining = remainingInventorySlots(existingCount + pendingCount, limit);
    if (remaining <= 0) return;
    setSlots((prev) => ({
      ...prev,
      [categoryId]: [...(prev[categoryId] || []), createInventorySlotId()],
    }));
  };

  const removeBox = (setSlots, setFiles, categoryId, slotId) => {
    setSlots((prev) => {
      const list = prev[categoryId] || [];
      if (list.length <= 1) return prev;
      return { ...prev, [categoryId]: list.filter((id) => id !== slotId) };
    });
    setFiles((prev) =>
      prev.filter(
        (file) => !(file.inventoryCategory === categoryId && file.inventorySlotId === slotId)
      )
    );
  };

  const roomForImages = remainingInventorySlots(
    existingImages + imageFiles.length,
    INVENTORY_UPLOAD_LIMITS.images
  );
  const roomForVideos = remainingInventorySlots(
    existingVideos + videoFiles.length,
    INVENTORY_UPLOAD_LIMITS.videos
  );
  const roomForPdfs = remainingInventorySlots(
    existingPdfs + pdfFiles.length,
    INVENTORY_UPLOAD_LIMITS.pdfs
  );

  return (
    <form className="inv-form" onSubmit={onSubmit}>
      <h3 className="inv-section-title inv-section-title--repo">{title}</h3>
      {intro && <p className="inv-upload-intro">{intro}</p>}

      <section className="inv-upload-section inv-upload-section--repo">
        <h4 className="inv-upload-group-title">Repossessed Assets Photos</h4>
        <p className="inv-upload-hint">
          {existingImages} saved · {roomForImages} image slot{roomForImages === 1 ? "" : "s"} left
        </p>
        <CategoryBoxes
          categories={INVENTORY_IMAGE_CATEGORIES}
          slots={imageSlots}
          files={imageFiles}
          savedPaths={showSavedPreviews ? confirmation?.inventoryImages : []}
          meta={meta}
          kind="image"
          accept="image/*"
          chooseLabel="Upload Photo"
          existingCount={existingImages}
          limit={INVENTORY_UPLOAD_LIMITS.images}
          submitting={submitting}
          onAddFiles={(picked, categoryId, slotId) =>
            addFiles(
              setImageFiles,
              existingImages,
              INVENTORY_UPLOAD_LIMITS.images,
              picked,
              categoryId,
              slotId
            )
          }
          onRemoveFile={(file) => removeFile(setImageFiles, file)}
          onAddBox={(categoryId) =>
            addBox(setImageSlots, existingImages, imageFiles.length, INVENTORY_UPLOAD_LIMITS.images, categoryId)
          }
          onRemoveBox={(categoryId, slotId) =>
            removeBox(setImageSlots, setImageFiles, categoryId, slotId)
          }
        />
      </section>

      <section className="inv-upload-section inv-upload-section--repo">
        <h4 className="inv-upload-group-title">Repossessed Assets Videos</h4>
        <p className="inv-upload-hint">
          {existingVideos} saved · {roomForVideos} video slot{roomForVideos === 1 ? "" : "s"} left
        </p>
        <CategoryBoxes
          categories={INVENTORY_VIDEO_CATEGORIES}
          slots={videoSlots}
          files={videoFiles}
          savedPaths={showSavedPreviews ? confirmation?.inventoryVideos : []}
          meta={meta}
          kind="video"
          accept="video/*"
          chooseLabel="Upload Video"
          existingCount={existingVideos}
          limit={INVENTORY_UPLOAD_LIMITS.videos}
          submitting={submitting}
          onAddFiles={(picked, categoryId, slotId) =>
            addFiles(
              setVideoFiles,
              existingVideos,
              INVENTORY_UPLOAD_LIMITS.videos,
              picked,
              categoryId,
              slotId
            )
          }
          onRemoveFile={(file) => removeFile(setVideoFiles, file)}
          onAddBox={(categoryId) =>
            addBox(setVideoSlots, existingVideos, videoFiles.length, INVENTORY_UPLOAD_LIMITS.videos, categoryId)
          }
          onRemoveBox={(categoryId, slotId) =>
            removeBox(setVideoSlots, setVideoFiles, categoryId, slotId)
          }
        />
      </section>

      <section className="inv-upload-section">
        <h4 className="inv-upload-group-title">Pre/Post Inventory &amp; Bills</h4>
        <p className="inv-upload-hint">
          {existingPdfs} saved · {roomForPdfs} PDF slot{roomForPdfs === 1 ? "" : "s"} left
        </p>
        <CategoryBoxes
          categories={INVENTORY_PDF_CATEGORIES}
          slots={pdfSlots}
          files={pdfFiles}
          savedPaths={showSavedPreviews ? confirmation?.inventoryPdfs : []}
          meta={meta}
          kind="pdf"
          accept="application/pdf,.pdf"
          chooseLabel="Upload PDF"
          existingCount={existingPdfs}
          limit={INVENTORY_UPLOAD_LIMITS.pdfs}
          submitting={submitting}
          onAddFiles={(picked, categoryId, slotId) =>
            addFiles(
              setPdfFiles,
              existingPdfs,
              INVENTORY_UPLOAD_LIMITS.pdfs,
              picked,
              categoryId,
              slotId
            )
          }
          onRemoveFile={(file) => removeFile(setPdfFiles, file)}
          onAddBox={(categoryId) =>
            addBox(setPdfSlots, existingPdfs, pdfFiles.length, INVENTORY_UPLOAD_LIMITS.pdfs, categoryId)
          }
          onRemoveBox={(categoryId, slotId) =>
            removeBox(setPdfSlots, setPdfFiles, categoryId, slotId)
          }
        />
      </section>

      {onSelectYard &&
      (parkingYardRequired ||
        !(confirmation?.parkingYardId || confirmation?.parkingYardName)) ? (
        <ParkingYardSearchSection
          selectedYard={selectedYard}
          onSelect={onSelectYard}
          required={parkingYardRequired}
        />
      ) : null}

      {success && <p className="inv-success">{success}</p>}

      <div className="inv-form-actions">
        <button
          type="submit"
          className="inv-submit"
          disabled={
            submitting ||
            (parkingYardRequired && !selectedYard?.id && !String(selectedYard?.name || "").trim())
          }
        >
          {submitting ? "Uploading…" : submitLabel}
        </button>
      </div>
    </form>
  );
}
